import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogTrigger,
} from '@/components/ui/dialog';
import DashPencil from '@/assets/icons/DashPencil';
import clsx from 'clsx';
import { formatNorwegianDate } from '@/utils/dates';
import { Skeleton } from '@/components/ui/skeleton';

const getFormattedColorName = (value) => {
  switch (value?.toLowerCase()) {
    case 'dark green':
      return 'Grønn';
    case 'light green':
      return 'Grønn';
    case 'yellow':
      return 'Gul';
    case 'orange':
      return 'Gul';
    case 'red':
      return 'Rød';
    default:
      return value;
  }
};

const energyMarks = [
  { value: 1, label: 'A' },
  { value: 2, label: 'B' },
  { value: 3, label: 'C' },
  { value: 4, label: 'D' },
  { value: 5, label: 'E' },
  { value: 6, label: 'F' },
  { value: 7, label: 'G' },
];
const heatingMarks = [
  { value: 1, label: 'Grønn' },
  { value: 2, label: 'Gul' },
  { value: 3, label: 'Rød' },
];

const heatingMarksClassNames = {
  empty: 'border-gray-400 bg-gray-400 text-white',
  Grønn: 'border-green-600 bg-green-600 text-white',
  Gul: 'border-yellow-300 bg-yellow-300 text-black',
  Rød: 'border-[#FF2A00] bg-[#FF2A00] text-white',
};

// EU standard energy score colors (A-G scale from 2026)
const energyScoreClassNames = {
  A: 'border-[#006B3C] bg-[#006B3C] text-white', // dark green
  B: 'border-[#4CAF50] bg-[#4CAF50] text-white', // light green
  C: 'border-[#8BC34A] bg-[#8BC34A] text-white', // yellow-green
  D: 'border-[#FFEB3B] bg-[#FFEB3B] text-black', // yellow
  E: 'border-[#FF9800] bg-[#FF9800] text-white', // orange
  F: 'border-[#F44336] bg-[#F44336] text-white', // red-orange
  G: 'border-[#D32F2F] bg-[#D32F2F] text-white', // red
  empty: 'border-gray-400 bg-gray-400 text-white',
};

const heatingTexts = [
  {
    Grønn: 'Meget lavt energibehov og høy andel fornybar oppvarming.',
    Gul: 'Meget lavt energibehov og delvis fornybar oppvarming.',
    Rød: 'Meget lavt energibehov og lav andel fornybar oppvarming.',
  },
  {
    Grønn: 'Lavt energibehov og høy andel fornybar oppvarming.',
    Gul: 'Lavt energibehov og delvis fornybar oppvarming.',
    Rød: 'Lavt energibehov og lav andel fornybar oppvarming.',
  },
  {
    Grønn: 'Middels energibehov og høy andel fornybar oppvarming.',
    Gul: 'Middels energibehov og delvis fornybar oppvarming.',
    Rød: 'Middels energibehov og lav andel fornybar oppvarming.',
  },
  {
    Grønn: 'Moderat høyt energibehov og høy andel fornybar oppvarming.',
    Gul: 'Moderat høyt energibehov og delvis fornybar oppvarming.',
    Rød: 'Moderat høyt energibehov og lav andel fornybar oppvarming.',
  },
  {
    Grønn: 'Høyt energibehov og høy andel fornybar oppvarming.',
    Gul: 'Høyt energibehov og delvis fornybar oppvarming.',
    Rød: 'Høyt energibehov og lav andel fornybar oppvarming.',
  },
  {
    Grønn: 'Svært høyt energibehov og høy andel fornybar oppvarming.',
    Gul: 'Svært høyt energibehov og delvis fornybar oppvarming.',
    Rød: 'Svært høyt energibehov og lav andel fornybar oppvarming.',
  },
  {
    Grønn: 'Meget høyt energibehov og høy andel fornybar oppvarming.',
    Gul: 'Meget høyt energibehov og delvis fornybar oppvarming.',
    Rød: 'Meget høyt energibehov og lav andel fornybar oppvarming.',
  },
];

// Helper function to check if certificate is from new format (after 2026-01-01)
const isNewFormatCertificate = (certificateDate) => {
  if (!certificateDate) return false;
  const cutoffDate = new Date('2026-01-01T00:00:00');
  const certDate = new Date(certificateDate);
  return certDate >= cutoffDate;
};

const EnergyMark = ({
  activeProperty,
  unitFeatures,
  createdTime,
  defaultEnergyScore,
  defaultHeatingScore,
  onEnergyDataChange,
  isCustomFacts,
}) => {
  const [isCustomMarkings, setIsCustomMarkings] = useState(isCustomFacts);
  const [open, setOpen] = useState(false);
  const [activeEnergyOption, setActiveEnergyOption] = useState(energyMarks[0]);
  const [activeHeatingOption, setActiveHeatingOption] = useState(
    heatingMarks[0]
  );
  const [energyScore, setEnergyScore] = useState();
  const [heatingScore, setHeatingScore] = useState();
  const handleUpdate = async () => {
    setEnergyScore(activeEnergyOption);
    setHeatingScore(activeHeatingOption);
    const success = await onEnergyDataChange({
      energyScore: activeEnergyOption.value,
      heatingScore: activeHeatingOption.value,
    });
    if (success) {
      setOpen(false);
      setIsCustomMarkings(true);
    }
  };

  useEffect(() => {
    if (unitFeatures?.energyScore) {
      const isNewFormat = isNewFormatCertificate(
        createdTime || unitFeatures?.certificateIssueDate
      );
      const energyScoreLetter =
        typeof unitFeatures.energyScore === 'string'
          ? unitFeatures.energyScore.toUpperCase()
          : unitFeatures.energyScore;

      const foundEnergyMark = energyMarks.find(
        (el) =>
          energyScoreLetter.includes(el.label) || el.label === energyScoreLetter
      );

      setEnergyScore(foundEnergyMark);

      if (isNewFormat) {
        // New format (after 2026-01-01): heatingScore can be null, use energy score colors
        if (
          unitFeatures.heatingScore &&
          typeof unitFeatures.heatingScore === 'object' &&
          unitFeatures.heatingScore.en
        ) {
          // Still has heatingScore object (backward compatibility)
          setHeatingScore(
            heatingMarks.find((el) =>
              getFormattedColorName(unitFeatures.heatingScore.en).includes(
                el.label
              )
            )
          );
        } else {
          // heatingScore is null or missing in new format
          setHeatingScore(undefined);
        }
      } else if (
        unitFeatures.heatingScore &&
        typeof unitFeatures.heatingScore === 'object' &&
        unitFeatures.heatingScore.en
      ) {
        // Old format (before 2026-01-01): heatingScore should be an object
        setHeatingScore(
          heatingMarks.find((el) =>
            getFormattedColorName(unitFeatures.heatingScore.en).includes(
              el.label
            )
          )
        );
      } else {
        // Handle null heatingScore gracefully even in old format
        setHeatingScore(undefined);
      }
    } else {
      // Fallback to custom/default values
      setEnergyScore(
        defaultEnergyScore?.value ? defaultEnergyScore : undefined
      );
      setHeatingScore(
        defaultHeatingScore?.value ? defaultHeatingScore : undefined
      );
    }
  }, [defaultEnergyScore, defaultHeatingScore, unitFeatures, createdTime]);

  useEffect(() => {
    setIsCustomMarkings(isCustomFacts);
  }, [isCustomFacts]);

  if (!activeProperty?.boligmappaNumber) {
    return <Skeleton className="bg-secondary h-[424px] md:h-[239px] w-full" />;
  }

  return (
    <div>
      <h3 className="text-xl font-medium mb-[13px]">Energimerking</h3>
      <Card className="flex flex-col md:grid md:grid-cols-2 p-[20px] shadow-none">
        <div className="flex flex-col items-center justify-center md:border-r px-6 py-3 md:py-5 gap-3">
          <div
            className={clsx([
              'relative w-[60px] h-[60px] rounded',
              // Use energy score color for new format (after 2026-01-01) or when heatingScore is null
              // Otherwise use heating score color for old format
              (() => {
                const isNewFormat = isNewFormatCertificate(
                  createdTime || unitFeatures?.certificateIssueDate
                );
                if (isNewFormat && energyScore?.label) {
                  // New format: use energy score colors
                  return (
                    energyScoreClassNames[energyScore.label] ||
                    energyScoreClassNames.empty
                  );
                }
                if (heatingScore?.label) {
                  // Old format or has heatingScore: use heating score colors
                  return heatingMarksClassNames[heatingScore.label];
                }
                return heatingMarksClassNames.empty;
              })(),
            ])}
          >
            <p className="absolute text-[37px] leading-[40px] font-bold left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              {energyScore?.label || '?'}
            </p>
          </div>
          {energyScore && !isCustomMarkings && (
            <p className="text-[15px] text-center text-foreground opacity-65 max-w-[230px]">
              Oppdatert {formatNorwegianDate(createdTime)}.
            </p>
          )}
          {energyScore && isCustomMarkings && (
            <p className="text-[15px] text-center text-foreground opacity-65 max-w-[230px]">
              Oppgitt av boligeier
            </p>
          )}
        </div>
        <div className="flex items-start justify-between flex-col gap-4 py-4 md:py-2 px-2 md:px-6">
          <div>
            <h3 className="text-[17px] text-black font-medium">
              {energyScore
                ? (() => {
                    const isNewFormat = isNewFormatCertificate(
                      createdTime || unitFeatures?.certificateIssueDate
                    );
                    if (isNewFormat) {
                      // New format: show only energy score
                      return `Din bolig har merkingen ${energyScore.label}`;
                    }
                    // Old format: show both if available
                    return heatingScore?.label
                      ? `Din bolig har merkingen ${energyScore.label}, ${heatingScore.label}`
                      : `Din bolig har merkingen ${energyScore.label}`;
                  })()
                : 'Din bolig mangler energimerking'}
            </h3>
            <p className="text-sm">
              {energyScore
                ? (() => {
                    const isNewFormat = isNewFormatCertificate(
                      createdTime || unitFeatures?.certificateIssueDate
                    );
                    if (isNewFormat) {
                      // New format: show EU standard message
                      return `Din bolig har energimerking ${energyScore.label} i henhold til EU-standard.`;
                    }
                    // Old format: show heating text if available
                    return heatingScore?.label
                      ? heatingTexts[energyScore.value - 1][heatingScore.label]
                      : `Din bolig har energimerking ${energyScore.label}.`;
                  })()
                : 'Vi kan ikke finne energimerking på denne eiendommen i Enovas database. Du kan selv oppgi korrekt merking og laste opp rapport i boligens boligmappe.'}
            </p>
          </div>
          {energyScore && (
            <p className="text-sm opacity-65">
              Har du gjort endringer nylig som kan ha påvirket merkingen? Da kan
              du oppdatere merkingen selv under.
            </p>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="flex justify-between text-sm font-normal border-red-400 w-full px-[11px] py-[9px]"
              >
                <p>Oppdater merking</p>
                <DashPencil />
              </Button>
            </DialogTrigger>
            <DialogContent className="flex flex-col text-black gap-[18px] sm:gap-4 items-start sm:max-w-[425px] p-7">
              <div className="flex flex-col gap-2">
                <DialogTitle asChild>
                  <h3 className="font-medium text-[21px]">
                    Sett din egen energimerking
                  </h3>
                </DialogTitle>
                <p className="text-sm pr-2">
                  Er det feil energimerking, kan du oppdatere den helt selv. Vær
                  obs på at vi gjør det tydelig at denne er selvrapportert.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h4 className="text-base font-medium">Energikarakter</h4>
                <div className="flex gap-[7px]">
                  {energyMarks.map((mark) => (
                    <Button
                      key={mark.label}
                      variant="outline"
                      className={clsx([
                        'text-base text-gray-500 font-normal border-color-grey-400 p-[13px] shadow-none hover:bg-gray-200 ',
                        activeEnergyOption?.label === mark.label &&
                          'bg-gray-200 border-black text-black',
                      ])}
                      onClick={() => setActiveEnergyOption(mark)}
                    >
                      {mark.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <h4 className="text-base font-medium">Oppvarmingskarakter</h4>
                <div className="flex gap-[7px]">
                  {heatingMarks.map((mark) => (
                    <Button
                      key={mark.label}
                      variant="outline"
                      className={clsx([
                        'text-base text-gray-500 font-normal border-color-grey-400 p-[13px] shadow-none hover:bg-gray-200 ',
                        activeHeatingOption?.label === mark.label &&
                          'bg-gray-200 border-black text-black',
                      ])}
                      onClick={() => setActiveHeatingOption(mark)}
                    >
                      {mark.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <h4 className="text-base font-medium">Din tilstand</h4>
                <div className="flex flex-col items-start p-[15px] gap-[7px] border rounded">
                  <p
                    className={clsx([
                      heatingMarksClassNames[
                        activeHeatingOption?.label || 'empty'
                      ],
                      '!bg-transparent border !text-black border-[2px] rounded px-2 py-1',
                    ])}
                  >
                    {activeEnergyOption
                      ? `${activeEnergyOption.label}, ${activeHeatingOption.label}`
                      : '?'}
                  </p>
                  <p className="text-sm">
                    {
                      heatingTexts[activeEnergyOption.value - 1][
                        activeHeatingOption.label
                      ]
                    }
                  </p>
                </div>
              </div>
              <Button
                className="border-red-400 w-full text-base leading-6 h-auto p-3"
                variant="outline"
                onClick={handleUpdate}
              >
                Lagre endringer
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </Card>
    </div>
  );
};

export default EnergyMark;
